import React, { useState, useEffect } from "react";
import { api } from "../utils/api";
import { showErrorToast, showSuccessToast } from "../utils/toast";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TableContainer,
  Paper,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { COLORS } from "../utils/colors";
import { useRefreshSessionToken } from "../hooks/useRefreshSessionToken";

const AdminFixStripeProduct = ({ subscriptionId, hasStripeId, onSuccess }) => {
  const refreshSessionToken = useRefreshSessionToken();
  const [open, setOpen] = useState(false);
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState("");
  const [newPrice, setNewPrice] = useState("");
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      setProductsLoading(true);
      api("v1/product?includeAll=true", "GET")
        .then((res) => {
          if (res?.data?.success && res.data.data?.allProducts) {
            const subs = (res.data.data.allProducts || []).filter((p) => p.type === "subscription");
            setProducts(subs);
            if (subs.length > 0 && !selectedProductId) {
              setSelectedProductId(subs[0]._id);
              setNewPrice(String(subs[0].price ?? ""));
            }
          }
        })
        .catch(() => setProducts([]))
        .finally(() => setProductsLoading(false));
    }
  }, [open]);

  useEffect(() => {
    if (selectedProductId && products.length > 0) {
      const p = products.find((x) => x._id === selectedProductId);
      if (p) setNewPrice(String(p.price ?? ""));
    }
  }, [selectedProductId, products.length]);

  const handleOpen = () => {
    setOpen(true);
    setPreview(null);
    setError(null);
    setSelectedProductId("");
    setNewPrice("");
  };

  const handleClose = () => {
    setOpen(false);
    setPreview(null);
    setError(null);
  };

  const selectedProduct = products.find((p) => p._id === selectedProductId);
  const priceNum = parseFloat(newPrice);
  const canPreview = selectedProductId && !Number.isNaN(priceNum) && priceNum > 0;

  const handlePreview = async () => {
    if (!subscriptionId || !canPreview) return;
    setLoading(true);
    setError(null);
    setPreview(null);
    try {
      if (!(await refreshSessionToken())) {
        showErrorToast(
          "Could not get an API token. Refresh the page, or sign out and sign in again if this keeps happening."
        );
        setLoading(false);
        return;
      }
      const response = await api(`v1/subscription/${subscriptionId}/fix-stripe-product`, "POST", {
        dryRun: true,
        targetProductName: selectedProduct?.name,
        newPrice: priceNum,
      });
      if (response?.data?.success) {
        setPreview(response.data.data);
      } else {
        setError(response?.data?.message || "Failed to preview");
      }
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.data?.error ||
        err?.message ||
        "Failed to preview";
      setError(msg);
      showErrorToast(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleApply = async () => {
    if (!subscriptionId || !canPreview) return;
    setApplying(true);
    setError(null);
    try {
      if (!(await refreshSessionToken())) {
        showErrorToast(
          "Could not get an API token. Refresh the page, or sign out and sign in again if this keeps happening."
        );
        setApplying(false);
        return;
      }
      const response = await api(`v1/subscription/${subscriptionId}/fix-stripe-product`, "POST", {
        dryRun: false,
        targetProductName: selectedProduct?.name,
        newPrice: priceNum,
      });
      if (response?.data?.success) {
        showSuccessToast(response.data.message || "Stripe subscription updated successfully");
        handleClose();
        if (onSuccess) onSuccess();
      } else {
        setError(response?.data?.message || "Failed to apply");
        showErrorToast(response?.data?.message || "Failed to apply");
      }
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.data?.error ||
        err?.message ||
        "Failed to apply";
      setError(msg);
      showErrorToast(msg);
    } finally {
      setApplying(false);
    }
  };

  if (!hasStripeId) return null;

  return (
    <>
      <Button
        variant="outlined"
        size="small"
        onClick={handleOpen}
        sx={{ borderColor: COLORS.priorityBlue?.hex || "#1976d2", color: COLORS.priorityBlue?.hex || "#1976d2" }}
      >
        Fix Stripe Product
      </Button>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>Fix Stripe Product & Price</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Correct a subscription that was created with the wrong product or price. Select the target product and
            set the new price. No immediate charge — new rate applies from next billing cycle.
          </Typography>

          {productsLoading ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 2 }}>
              <CircularProgress size={24} />
              <Typography>Loading products...</Typography>
            </Box>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mb: 2 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Target Product</InputLabel>
                <Select
                  value={selectedProductId}
                  label="Target Product"
                  onChange={(e) => setSelectedProductId(e.target.value)}
                >
                  {products.map((p) => (
                    <MenuItem key={p._id} value={p._id}>
                      {p.name} {p.price != null ? `($${p.price})` : ""}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="New Price ($/month)"
                type="number"
                size="small"
                value={newPrice}
                onChange={(e) => setNewPrice(e.target.value)}
                inputProps={{ min: 0, step: 0.01 }}
                helperText="Amount in dollars (e.g. 79)"
              />
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {!preview && !loading && canPreview && (
            <Button
              variant="contained"
              onClick={handlePreview}
              disabled={loading}
              sx={{ backgroundColor: COLORS.priorityBlue?.hex || "#1976d2" }}
            >
              Preview (Dry Run)
            </Button>
          )}

          {loading && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 2 }}>
              <CircularProgress size={24} />
              <Typography>Loading preview...</Typography>
            </Box>
          )}

          {preview && !loading && (
            <Box sx={{ mt: 2 }}>
              {preview.alreadyCorrect ? (
                <Alert severity="info">{preview.message}</Alert>
              ) : (
                <>
                  <Button
                    size="small"
                    onClick={() => setPreview(null)}
                    sx={{ mb: 1 }}
                  >
                    Change selection
                  </Button>
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    This will modify the live Stripe subscription. No immediate charge — new rate applies from next
                    billing cycle.
                  </Alert>

                  <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                    <Table size="small">
                      <TableBody>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>Current</TableCell>
                          <TableCell>
                            ${preview.preview?.currentAmount?.toFixed(2) ?? "—"} /{" "}
                            {preview.preview?.interval ?? "month"}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>Target</TableCell>
                          <TableCell>
                            ${preview.preview?.targetAmount?.toFixed(2) ?? "—"} /{" "}
                            {preview.preview?.interval ?? "month"} — {preview.preview?.targetProductName}
                          </TableCell>
                        </TableRow>
                        {preview.preview?.wouldCreateProduct && (
                          <TableRow>
                            <TableCell sx={{ fontWeight: 600 }}>Action</TableCell>
                            <TableCell>
                              Will create new Stripe product &quot;{preview.preview?.targetProductName}&quot; and
                              price
                            </TableCell>
                          </TableRow>
                        )}
                        {preview.dryRun && (
                          <TableRow>
                            <TableCell colSpan={2}>
                              <Typography variant="caption" color="text.secondary">
                                Dry run — no changes made. Click Apply to commit.
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          {preview && !preview.alreadyCorrect && (
            <Button
              variant="contained"
              onClick={handleApply}
              disabled={applying}
              sx={{ backgroundColor: COLORS.priorityBlue?.hex || "#1976d2" }}
            >
              {applying ? (
                <>
                  <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} /> Applying...
                </>
              ) : (
                "Apply Changes"
              )}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AdminFixStripeProduct;
